import type { components } from "@/lib/api.gen";

export type ApiErrorBody = components["schemas"]["ApiError"];
export type ErrorKind = components["schemas"]["ErrorKind"];
export type MessageCode = components["schemas"]["MessageCode"];
export type FieldError = components["schemas"]["FieldError"];

/**
 * Typed wrapper around the backend's consistent error body. The backend
 * `message` is English and for debugging only — never render it. Use
 * {@link translateApiError} / {@link translateMessageCode} to get user-facing
 * (translated) text from `kind` / per-field `code`.
 */
export class ApiError extends Error {
  readonly kind: ErrorKind;
  readonly status: number;
  /** Field name -> list of coded errors for that field. */
  readonly invalidFields: Record<string, FieldError[]>;
  /** Form-level coded errors not tied to a specific field. */
  readonly generalErrors: FieldError[];

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.kind = body.kind;
    this.status = body.status;
    this.invalidFields = body.invalid_fields ?? {};
    this.generalErrors = body.general_errors ?? [];
  }

  /** Build an `ApiError` from an unknown thrown/returned value. */
  static from(error: unknown): ApiError {
    if (isApiErrorBody(error)) return new ApiError(error);
    return new ApiError({
      kind: "internal",
      message: typeof error === "string" ? error : JSON.stringify(error),
      status: 0,
    });
  }
}

function isApiErrorBody(error: unknown): error is ApiErrorBody {
  return (
    !!error &&
    typeof error === "object" &&
    "kind" in error &&
    "message" in error &&
    "status" in error
  );
}

/**
 * Unwrap an `openapi-fetch` `{ data, error }` result: returns `data` on success,
 * throws a typed {@link ApiError} otherwise. `error` is only set on non-2xx
 * responses, so a missing `error` means success (including empty 200 bodies).
 */
export function unwrap<T>(res: { data?: T; error?: unknown }): T {
  if (res.error !== undefined && res.error !== null) {
    throw ApiError.from(res.error);
  }
  return res.data as T;
}

/** Minimal shape of a next-intl translator that accepts a dot-path key. */
export type Translator = (key: string) => string;

/** Translate a single field/general error code to user-facing text. */
export function translateMessageCode(code: MessageCode, t: Translator): string {
  // `errors.code.*` / `errors.kind.*` are validated against the backend
  // MessageCode/ErrorKind enums by scripts/check-translations.ts.
  return t(`errors.code.${code}`);
}

/**
 * Pick the user-facing message for an error toast. Tolerates non-`ApiError`
 * throwables (un-migrated hooks / network failures) via a generic fallback.
 *
 * For `validation` errors the per-field specifics are shown inline at the field
 * (see `useApiFieldErrors` / `<FieldErrors>`), so the toast stays concise: a
 * form-level (general) message if there is one, else the generic
 * "check your inputs" prompt — it never repeats the field messages.
 */
export function translateApiError(error: unknown, t: Translator): string {
  if (error instanceof ApiError) {
    const general = error.generalErrors[0];
    if (error.kind === "validation") {
      return general
        ? translateMessageCode(general.code, t)
        : t("errors.kind.validation");
    }
    if (general) return translateMessageCode(general.code, t);
    const firstField = Object.values(error.invalidFields)[0]?.[0];
    if (firstField) return translateMessageCode(firstField.code, t);
    return t(`errors.kind.${error.kind}`);
  }
  return t("errors.generic");
}

/*
 * The global mutation toast (see providers.tsx) runs outside React and cannot
 * call `useTranslations`. Providers installs a module-scoped translator built
 * from the active locale/messages so the toast can translate by `kind`.
 */
let globalTranslator: Translator | null = null;

export function setGlobalErrorTranslator(t: Translator | null): void {
  globalTranslator = t;
}

/** Translate an error for the global toast, falling back if no translator is set. */
export function translateApiErrorGlobal(error: unknown): string {
  if (globalTranslator) return translateApiError(error, globalTranslator);
  if (error instanceof ApiError) return error.message;
  return "Ein Fehler ist aufgetreten";
}
